import _, {isEmpty} from 'lodash';

import {TRANSACTION_TYPE_VALUES} from "./constants";
import {
  SingleInvestmentStep,
  PaymentPlansStep,
  CostsConditionsStep,
  ExAnteCostsStep,
} from "./components/ExAnteStep/components/ExAnteStepV2";
import {OBJECT_TYPES, QUESTION_TYPE} from "../RiskProfiling/constants";


/**
 * Check if a portfolio has any buy/sell/switch (single investment) transactions.
 */
const hasSingleInvestmentTransactions = (portfolio) => {
  const {transactions} = portfolio;
  return [TRANSACTION_TYPE_VALUES.BUY, TRANSACTION_TYPE_VALUES.SELL, TRANSACTION_TYPE_VALUES.SWITCH]
    .some(type => !_.isEmpty(transactions[type]));
};

/**
 * Check if a portfolio has any payment plan transactions.
 */
const hasPaymentPlanTransactions = (portfolio) => {
  const {transactions} = portfolio;
  return [TRANSACTION_TYPE_VALUES.SAVINGS_PLAN, TRANSACTION_TYPE_VALUES.PAYOUT_PLAN, TRANSACTION_TYPE_VALUES.SWITCH_PLAN]
    .some(type => !_.isEmpty(transactions[type]));
};

/**
 * Check if a portfolio has at least one transaction of any type.
 */
export const hasAnyTransactions = (portfolio) => {
  return hasSingleInvestmentTransactions(portfolio) || hasPaymentPlanTransactions(portfolio);
};

const getExAnteQuestion = (portfolio, types) => {
  return {
    uid: 'ex_ante',
    type: QUESTION_TYPE.HIDDEN,
    portfolio,
    types,
    dataFetched: false,
    optional: false,
    isValid: function() {
      return !isEmpty(this.answer);
    },
  }
}
/**
 * Build dynamic steps for the ExAnte menu item in new design mode.
 *
 * For each portfolio with transactions, creates sub-steps grouped by depot name (parent):
 *   - Single Investment (if has buy/sell/switch)
 *   - Payment Plans (if has savings_plan/payout_plan/switch_plan)
 *   - Costs & Conditions (depot type cost info)
 *   - Ex-Ante Costs (ex-ante calculation)
 *
 * @param {Array} tradings - Array of portfolio objects with transactions
 * @returns {Array} Array of step definitions for the questionnaire
 */
export const buildExAnteV2Steps = (tradings) => {
  const portfoliosWithTransactions = tradings.filter(hasAnyTransactions);

  const dynamicSteps = [];

  portfoliosWithTransactions.forEach((portfolio) => {
    const portfolioId = portfolio.portfolioId;
    const depotName = _.get(portfolio, 'data.name', `Depot ${portfolioId}`);
    const hasBuySellSwitch = hasSingleInvestmentTransactions(portfolio);
    const hasPlans = hasPaymentPlanTransactions(portfolio);

    if (hasBuySellSwitch) {
      dynamicSteps.push({
        uid: `single-investment-step-${portfolioId}`,
        parent: depotName,
        category: 'Einmalanlage',
        step_content: SingleInvestmentStep,
        no_user_input: true,
        question: [{
          uid: 'single-investment-q',
          type: QUESTION_TYPE.HIDDEN,
          answer: true,
          portfolio,
        }]
      });
    }

    // Payment Plans — if depot has savings_plan/payout_plan/switch_plan
    if (hasPlans) {
      dynamicSteps.push({
        uid: `payment-plans-step-${portfolioId}`,
        parent: depotName,
        category: 'Zahlpläne',
        step_content: PaymentPlansStep,
        no_user_input: true,
        question: [{
          uid: 'payment-plans-q',
          type: QUESTION_TYPE.HIDDEN,
          answer: true,
          portfolio,
        }]
      });
    }

    // Costs & Conditions — depot type cost info
    dynamicSteps.push({
      uid: `costs-conditions-step-${portfolioId}`,
      parent: depotName,
      category: 'Kosten & Konditionen',
      step_content: CostsConditionsStep,
      no_user_input: true,
      question: [{
        uid: 'costs-conditions-q',
        type: QUESTION_TYPE.HIDDEN,
        answer: true,
        portfolio,
      }]
    });

    // Ex-Ante Costs — the actual ex-ante calculation for this portfolio
    if(hasBuySellSwitch) {
      dynamicSteps.push({
        uid: `ex-ante-step-single-trades-${portfolioId}`,
        parent: depotName,
        category: 'Ex-Ante Kosten K/V/T',
        step_content: ExAnteCostsStep,
        no_user_input: true,
        question: [
          getExAnteQuestion(portfolio, [OBJECT_TYPES.TRADING])
        ]
      });
    }

    if (hasPlans){
      dynamicSteps.push({
        uid: `ex-ante-step-plans-${portfolioId}`,
        parent: depotName,
        category: 'Ex-Ante Kosten Zahlpläne',
        step_content: ExAnteCostsStep,
        no_user_input: true,
        question: [
          getExAnteQuestion(portfolio, [OBJECT_TYPES.SAVINGS_PLAN, OBJECT_TYPES.PAYOUT_PLAN, OBJECT_TYPES.SWITCH_PLAN])
        ]
      });
    }
  });

  if(!_.isEmpty(dynamicSteps)) {
    _.last(dynamicSteps).question[0].withPrint = true;
  }

  return dynamicSteps;
};
